import { useMemo, useState, useCallback, useEffect } from 'react';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useWorkflowStore } from '@/state/workflow.state';
import { useModelBaseView } from '@/components/Recipe/FlowContext';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import type { IndexedUploadedAsset } from '@/components/Common/ImageList/ImageList';
import type { NodeId } from 'web';

interface UseBatchViewOptions {
  nodeId: NodeId;
  nodeName: string;
  allGenerations: IndexedUploadedAsset[];
  viewMode: NodeViewMode;
  selectedIndex?: number;
}

interface BatchInfo {
  batchNumber: number;
  totalBatches: number;
  sortedBatchIds: string[];
}

export const useBatchView = ({ nodeId, nodeName, allGenerations, viewMode, selectedIndex }: UseBatchViewOptions) => {
  const modelBaseView = useModelBaseView(nodeId);
  const { name: recipeName } = useWorkflowStore((state) => state.recipe);
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();
  // Group generations by batchId
  const generationsByBatchId = useMemo(() => {
    return allGenerations.reduce(
      (acc, generation) => {
        const batchId = generation.batchId;
        if (!batchId) {
          return acc;
        }
        if (!acc[batchId]) {
          acc[batchId] = [];
        }

        acc[batchId].push(generation);
        return acc;
      },
      {} as Record<string, IndexedUploadedAsset[]>,
    );
  }, [allGenerations]);

  // Get sorted batch IDs according to index of first file in each batch
  const sortedBatchIds = useMemo(() => {
    return Object.entries(generationsByBatchId)
      .sort((a, b) => a[1][0].globalIndex - b[1][0].globalIndex)
      .map(([batchId]) => batchId);
  }, [generationsByBatchId]);

  // Get initial batch ID - prefer batch containing selectedIndex, otherwise use last batch
  const getInitialBatchId = useCallback(() => {
    if (selectedIndex !== undefined && allGenerations[selectedIndex]) {
      const selectedFile = allGenerations[selectedIndex];
      return selectedFile.batchId;
    }
    return sortedBatchIds[sortedBatchIds.length - 1];
  }, [selectedIndex, allGenerations, sortedBatchIds]);

  // State for selected batch ID
  const [selectedBatchId, setSelectedBatchId] = useState<string | undefined>(getInitialBatchId);
  const [previousLastBatchId, setPreviousLastBatchId] = useState<string | undefined>(
    sortedBatchIds[sortedBatchIds.length - 1],
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  // On first load, if selectedIndex is provided, select the batch containing that file
  useEffect(() => {
    if (viewMode === NodeViewMode.Batch && sortedBatchIds.length > 0) {
      if (!hasInitialized && selectedIndex !== undefined && allGenerations[selectedIndex]) {
        const selectedFileBatchId = allGenerations[selectedIndex].batchId;
        if (selectedFileBatchId) {
          setSelectedBatchId(selectedFileBatchId);
        }
      }
      setHasInitialized(true);
    }
  }, [viewMode, sortedBatchIds, selectedBatchId, previousLastBatchId, selectedIndex, allGenerations, hasInitialized]);

  // If the last batch ID changed, a new batch was generated - select it automatically
  // Also handle case where currently selected batch was deleted
  useEffect(() => {
    if (!hasInitialized) {
      return;
    }
    const currentLastBatchId = sortedBatchIds[sortedBatchIds.length - 1];
    // Check if the currently selected batch still exists (not all associated generations were deleted)
    const selectedBatchExists = selectedBatchId && sortedBatchIds.includes(selectedBatchId);
    if (!selectedBatchExists && sortedBatchIds.length > 0) {
      setSelectedBatchId(sortedBatchIds[0]);
    } else if (currentLastBatchId !== previousLastBatchId) {
      // New batch was generated - select it automatically
      setSelectedBatchId(currentLastBatchId);
      setPreviousLastBatchId(currentLastBatchId);
    }
  }, [
    previousLastBatchId,
    setSelectedBatchId,
    setPreviousLastBatchId,
    sortedBatchIds,
    selectedBatchId,
    hasInitialized,
  ]);

  // Filter generations based on view mode
  const generations = useMemo(() => {
    if (viewMode === NodeViewMode.Batch && selectedBatchId) {
      return generationsByBatchId[selectedBatchId] || [];
    }
    return allGenerations;
  }, [viewMode, selectedBatchId, generationsByBatchId, allGenerations]);

  // Check if all batches contain only a single generation
  const isAllBatchesHaveSingleGeneration = useMemo(() => {
    return Object.values(generationsByBatchId).every((batch) => batch.length === 1);
  }, [generationsByBatchId]);

  // Calculate batch number and total batches for batch view mode
  const batchInfo = useMemo<BatchInfo | null>(() => {
    if (viewMode !== NodeViewMode.Batch || !selectedBatchId) {
      return null;
    }
    const currentBatchIndex = sortedBatchIds.indexOf(selectedBatchId);
    const batchNumber = currentBatchIndex >= 0 ? currentBatchIndex + 1 : 1;
    const totalBatches = sortedBatchIds.length;
    return { batchNumber, totalBatches, sortedBatchIds };
  }, [viewMode, selectedBatchId, sortedBatchIds]);

  // Navigate to previous batch
  const handlePreviousBatch = useCallback(() => {
    if (!batchInfo || !selectedBatchId) return;
    const currentIndex = sortedBatchIds.indexOf(selectedBatchId);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : sortedBatchIds.length - 1;
    setSelectedBatchId(sortedBatchIds[previousIndex]);
  }, [batchInfo, selectedBatchId, sortedBatchIds]);

  // Navigate to next batch
  const handleNextBatch = useCallback(() => {
    if (!batchInfo || !selectedBatchId) return;
    const currentIndex = sortedBatchIds.indexOf(selectedBatchId);
    const nextIndex = currentIndex < sortedBatchIds.length - 1 ? currentIndex + 1 : 0;
    setSelectedBatchId(sortedBatchIds[nextIndex]);
  }, [batchInfo, selectedBatchId, sortedBatchIds]);

  // Download all files in the current batch, or single file in single view mode
  const handleDownload = useCallback(() => {
    const downloadAsync = async () => {
      if (viewMode === NodeViewMode.Single) {
        // In single view mode, download only the selected file
        const asset = selectedIndex !== undefined ? modelBaseView.getDownloadableAssetAtIndex(selectedIndex) : null;
        if (asset) {
          await downloadAssetFromNode({
            url: asset.url,
            fileType: asset.type,
            nodeName,
            prefix: recipeName,
          });
        }
      } else {
        // In batch or all view mode, download files from the current view (filtered by batch if applicable)
        // Filter out non-downloadable assets (text and rendering)
        const downloadableAssets = generations.filter(
          (generation): generation is typeof generation & { url: string } =>
            generation.type !== 'text' && generation.type !== 'rendering' && 'url' in generation,
        );
        await downloadAssetsFromNodeWithProgress(downloadableAssets, nodeName, recipeName, batchInfo?.batchNumber);
      }
    };
    void downloadAsync();
  }, [
    viewMode,
    selectedIndex,
    modelBaseView,
    nodeName,
    recipeName,
    generations,
    batchInfo,
    downloadAssetFromNode,
    downloadAssetsFromNodeWithProgress,
  ]);

  return {
    generations,
    batchInfo,
    handlePreviousBatch,
    handleNextBatch,
    handleDownload,
    isAllBatchesHaveSingleGeneration,
  };
};
